import { useState, useContext, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { useApi } from '../features/api/useApi';
import {
  CampusContext,
  CampusContextType,
  CampusFeatureFlags,
  ThermalZoneTactic,
  ControlMode,
} from './CampusContext';
import { CONTROL_MODE } from '../config/control';

import configuration from '../config.js';

interface CampusContextProviderProps {
  children: ReactNode;
}

export const CampusContextProvider = ({ children }: CampusContextProviderProps) => {
  const { fetchJson } = useApi();
  const campusId = useSelector((state: any) => state.currentCampusId);
  const [campusInterconnects, setCampusInterconnects] = useState<string[]>([]);
  const [controlModes, setControlModes] = useState<ControlMode[]>([]);
  // const campusList = useSelector((state: any) => state.campuses);

  const {
    data: batteryData,
    // isLoading: batteryLoading,
    // error: batteryError,
  } = useQuery({
    queryKey: ['campus', campusId, 'battery'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/acBatteries`),
    enabled: !!campusId,
  });

  const { data: campusMetaData } = useQuery({
    queryKey: ['campus', campusId, 'meta'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/comfort`),
    enabled: !!campusId,
  });

  const { data: campusEnergyData } = useQuery({
    queryKey: ['campus', campusId, 'energy'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/energy`),
    enabled: !!campusId,
  });

  const { data: interconnectData } = useQuery({
    queryKey: ['interconnect'],
    queryFn: () => {
      if (campusInterconnects.length === 0) return;
      return fetchJson(
        `${configuration['backend_host']}/admin/monitor/${campusInterconnects[0]}/gridInterconnect/graphData/live`
      );
    },
    // refetchInterval: 5000,
    retryDelay: 5000,
    enabled: campusInterconnects.length > 0,
  });

  useEffect(() => {
    // console.log('campusEnergyData', campusEnergyData);
    setCampusInterconnects(
      campusEnergyData?.gridInterconnects?.map(
        (interconnect: any) => interconnect.gridInterconnectDescription.gridInterconnectEntityKey
      ) || []
    );
    if (!campusEnergyData || campusEnergyData.length === 0) return;
  }, [campusEnergyData]);

  // useEffect(() => {
  //   console.log('campusMetaData', campusMetaData);
  // }, [campusMetaData]);

  // useEffect(() => {
  //   console.log('campusInterconnects', campusInterconnects);
  // }, [campusInterconnects]);

  useEffect(() => {
    // console.log('interconnectData', interconnectData);
    getThermalZoneTactics();
  }, [interconnectData]);

  // useEffect(() => {
  //   console.log('controlModes', controlModes);
  // }, [controlModes]);

  const campusName = campusMetaData?.campusDescription?.longCampusDisplayName || 'Campus';

  const campusHasBattery = (batteryData: any) => {
    if (!batteryData || batteryData.length === 0 || batteryData?.gridInterconnects?.length === 0) {
      return false;
    } else if (batteryData?.gridInterconnects?.length > 0) {
      const hasBattery = batteryData.gridInterconnects.some(
        (interconnect: any) => interconnect.acBatteries.length > 0
      );
      return hasBattery;
    }
    return true;
  };

  const getThermalZoneTactics: () => ThermalZoneTactic[] = () => {
    if (!interconnectData) return [];
    const thermalZoneTactics = interconnectData?.blockCategories?.find(
      (category: any) => category.categoryName === 'ThermalZoneTactic'
    )?.blocks;
    const newThermalZoneTactics: ControlMode[] = [];

    for (const tactic of thermalZoneTactics || []) {
      const controlMode = assignControlMode(tactic);
      if (controlMode) {
        newThermalZoneTactics.push(controlMode);
      }
    }

    setControlModes(newThermalZoneTactics);

    return thermalZoneTactics || [];
  };

  const assignControlMode = (tactic: ThermalZoneTactic) => {
    const now = new Date();
    const start = new Date(tactic.startDateTime);
    const end = new Date(tactic.endDateTime);

    // if the tactic blockCode is not a valid CONTROL_MODE, return null
    // There are some unexpected blockCodes for example 'NotSelected'
    if (!CONTROL_MODE[tactic.blockCode.toUpperCase()]) {
      // console.warn(`Tactic blockCode ${tactic.blockCode} does not match any CONTROL_MODE`);
      return null;
    }

    if (now >= start && now <= end) {
      const untilDesc = () => {
        const untilString =
          end > now
            ? `Until ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : '';

        if (untilString.length > 0) {
          return { untilDesc: untilString };
        }
      };

      return {
        id: `${tactic.blockCode}-${tactic.startDateTime}`,
        mode: tactic.blockCode,
        isActive: true,
        duration: {
          startTime: tactic.startDateTime,
          endTime: tactic.endDateTime,
        },
        ...untilDesc(),
      };
    }
    return {
      id: `${tactic.blockCode}-${tactic.startDateTime}`,
      mode: tactic.blockCode,
      isActive: false,
      duration: {
        startTime: tactic.startDateTime,
        endTime: tactic.endDateTime,
      },
    };
  };

  const featureFlags: CampusFeatureFlags = {
    hasBattery: batteryData ? campusHasBattery(batteryData) : false,
  };

  const contextValue: CampusContextType = {
    meta: {
      campusName: campusName || '',
      campusId: campusId || '',
    },
    featureFlags,
    controlModes: controlModes,
  };

  return <CampusContext.Provider value={contextValue}>{children}</CampusContext.Provider>;
};

export const useCampusContext = (): CampusContextType => {
  const context = useContext(CampusContext);
  if (context === undefined) {
    throw new Error('useCampusContext must be used within a CampusContextProvider');
  }
  return context;
};

// hooks
export const useCampusFeatureFlags = (): CampusFeatureFlags => useCampusContext().featureFlags;
export const useCampusMeta = (): CampusContextType['meta'] => useCampusContext().meta;
export const useCampusControlModes = (): ControlMode[] | undefined =>
  useCampusContext().controlModes;
